import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';

import { visuallyHidden } from '@mui/utils';
import AuthContext from 'context/AuthContext';
import { format } from 'date-fns';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { User } from 'services/admin-users';
import { getOrganizationById, updatePermission } from 'services/business-setting-service';
import { formatNumber } from 'utils/utils';
import ButtonIcon from './ButtonIcon';
import CustomTableCell from './CustomTableCell';
import { CustomTableFontCell } from './CustomTableFontCell';
import { StyledTableSortLabel } from './TableSortedLabel';
import UserPresenceChip from './UserPresenceChip';

interface Column {
  id: string;
  title: string;
}

interface AdminMembersTableProps {
  data: any;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  onRefetch: () => void;
  onHandleRemoveMember: (id: string) => void;
}

const AdminMembersTable: React.FC<AdminMembersTableProps> = ({
  data,
  page,
  rowsPerPage,
  sortBy,
  sortOrder,
  onPageChange,
  onRowsPerPageChange,
  onRequestSort,
  onRefetch,
  onHandleRemoveMember
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { setOrganizationId, setOrganization } = useContext(AuthContext)
  const { data: organizationData } = useQuery(
    ['getOrganizationById', selectedUser?.organizationId],
    () => getOrganizationById(selectedUser?.organizationId),
    {
      enabled: !!selectedUser?.organizationId,
      staleTime: 30000,
      cacheTime: 5 * 60 * 1000,
    }
  );
  const columns: Column[] = [
    { id: 'organizationName', title: t('ADMIN_USER_ORGANIZATION') },
    { id: 'name', title: t('ADMIN_USER_NAME') },
    { id: 'phone', title: t('ADMIN_USER_PHONE') },
    { id: 'email', title: t('ADMIN_USER_EMAIL') },
    { id: 'authorization', title: t('ADMIN_USER_ROLE') },
    { id: 'lastSeen', title: t('ADMIN_USER_PRESENCE') },
    { id: 'projectCount', title: t('ADMIN_USER_PROJECT_COUNT') },
    { id: 'revisionCount', title: t('ADMIN_USER_REVISION_COUNT') },
    { id: 'updatedAt', title: t('ADMIN_USER_UPDATED_AT') },
  ];

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleActionClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleViewOrganization = () => {
    if (selectedUser) {
      localStorage.setItem('organizationId', selectedUser.organizationId);
      setOrganizationId(selectedUser.organizationId);
      setOrganization(organizationData);
      navigate(`/admin/business/${selectedUser.organizationId}/projects`);
      handleActionClose();
    }
  };

  const handleBanUser = async () => {
    if (!selectedUser) return;
    try {
      await updatePermission(selectedUser.id, 'banned', selectedUser.organizationId);
      onRefetch();
    } catch (error) {
      console.error('Error banning user:', error);
    }
    handleActionClose();
  };

  const handleRemoveMember = async () => {
    if (!selectedUser?.id) return;
    try {
    
      onHandleRemoveMember(selectedUser.id);
    } catch (error) {
      console.error('Error removing member:', error);
    }
    handleActionClose();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
  };

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
            {columns.map((column) => (
              <CustomTableFontCell key={column.id}>
                <StyledTableSortLabel
                  active={sortBy === column.id}
                  direction={sortBy === column.id ? sortOrder : 'asc'}
                  onClick={(event) => onRequestSort(event, column.id)}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {column.title}
                  </Typography>
                  {sortBy === column.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
            ))}
            <CustomTableFontCell></CustomTableFontCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.data.map((user: any) => (
            <TableRow key={`user-${user.id}`}>
              <CustomTableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  {user.organizationImage && (
                    <img
                      src={user.organizationImage}
                      alt="organization"
                      style={{ width: '20px', height: '20px', borderRadius: '10%' }}
                    />
                  )}
                  <Typography variant="subtitle1">
                    {user.organizationName}
                  </Typography>
                </Box>
              </CustomTableCell>
              <CustomTableCell>{user.name || '-'}</CustomTableCell>
              <CustomTableCell>{user.phone || '-'}</CustomTableCell>
              <CustomTableCell>{user.email}</CustomTableCell>
              <CustomTableCell>{user.role}</CustomTableCell>
              <CustomTableCell>
                <UserPresenceChip lastSeen={user.lastSeen} />
              </CustomTableCell>
              <CustomTableCell>{formatNumber(user.projectCount)}</CustomTableCell>
              <CustomTableCell>{formatNumber(user.revisionCount)}</CustomTableCell>
              <CustomTableCell>{formatDate(user.updatedAt)}</CustomTableCell>
              <CustomTableCell>
                <ButtonIcon
                  onClick={(e) => handleActionClick(e, user)}
                  size="small"
                  isActive={selectedUser?.id === user.id ? true : false}
                  aria-controls={anchorEl ? "long-menu" : undefined}
                  aria-expanded={anchorEl ? "true" : undefined}
                  aria-haspopup="true"
                >
                  <MoreHorizIcon />
                </ButtonIcon>
              </CustomTableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[20, 50, 100]}
        component="div"
        count={data?.paging.total || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('ADMIN_USER_OF')} ${count}`}
      />
      <Menu
        sx={{ fontSize: '14px !important' }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionClose}
      >
        {/* <MenuItem onClick={handleViewOrganization}>
          {t('ADMIN_USER_VIEW_ORGANIZATION')}
        </MenuItem> */}
        <MenuItem onClick={handleRemoveMember}>
          {t('BUSINESS_SETTING_MEMBER_REMOVE_FROM_STAFF')}
        </MenuItem>

      </Menu>
    </>
  );
};

export default AdminMembersTable; 